import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

import type { WidgetConfig } from "./widget-config";

/** Minimal `SpeechRecognition` surface for WebKit + standard APIs (strict typing without `any`). */
interface SpeechRecognitionResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  readonly [index: number]: { readonly transcript: string } | undefined;
}

interface SpeechRecognitionEventLike {
  readonly resultIndex: number;
  readonly results: ArrayLike<SpeechRecognitionResultLike>;
}

interface SpeechRecognitionErrorEventLike {
  readonly error: string;
}

interface SpeechRecognitionLike extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((ev: SpeechRecognitionEventLike) => void) | null;
  onerror: ((ev: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getSpeechRecognitionConstructor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") {
    return null;
  }
  const record = window as unknown as Record<string, unknown>;
  const ctor = (record.SpeechRecognition ?? record.webkitSpeechRecognition) as
    | SpeechRecognitionCtor
    | undefined;
  return typeof ctor === "function" ? ctor : null;
}

function subscribeToNoop(): () => void {
  return () => {
    /* no-op: capability is static for the page lifetime */
  };
}

function hasServerSttCapability(): boolean {
  if (typeof window === "undefined") return false;
  const navAny = window.navigator as Navigator | undefined;
  if (!navAny || !navAny.mediaDevices || typeof navAny.mediaDevices.getUserMedia !== "function") {
    return false;
  }
  const w = window as unknown as { MediaRecorder?: unknown };
  return typeof w.MediaRecorder === "function";
}

function getAnySttSupportedSnapshot(): boolean {
  return hasServerSttCapability() || getSpeechRecognitionConstructor() !== null;
}

function getAnySttSupportedServerSnapshot(): boolean {
  return false;
}

function pickBase64Audio(payload: Record<string, unknown>): string | null {
  const candidates = ["audio_base64", "audioBase64", "audio", "data", "content", "b64"] as const;
  for (const key of candidates) {
    const v = payload[key];
    if (typeof v === "string" && v.length > 32) {
      return v;
    }
  }
  return null;
}

function stripDataUrlBase64(raw: string): string {
  const s = raw.trim();
  const marker = "base64,";
  const idx = s.indexOf(marker);
  if (idx !== -1) {
    return s.slice(idx + marker.length).trim();
  }
  return s;
}

function decodeBase64ToBlob(base64: string, mime: string): Blob {
  const normalized = stripDataUrlBase64(base64);
  const binary = atob(normalized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

/**
 * Picks the best MediaRecorder mime type the browser will accept. Safari only
 * recently shipped MediaRecorder.isTypeSupported; if it's missing we just
 * default to the browser-chosen mime.
 */
function pickRecorderMime(): string {
  if (typeof window === "undefined") return "";
  const w = window as unknown as { MediaRecorder?: { isTypeSupported?: (m: string) => boolean } };
  const isSupported = w.MediaRecorder?.isTypeSupported?.bind(w.MediaRecorder);
  if (!isSupported) return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
    "audio/mpeg",
  ];
  for (const c of candidates) {
    try {
      if (isSupported(c)) return c;
    } catch {
      /* ignore */
    }
  }
  return "";
}

export interface UseWidgetVoiceResult {
  readonly autoRead: boolean;
  readonly toggleAutoRead: () => void;
  readonly isListening: boolean;
  readonly isTranscribing: boolean;
  readonly startListening: (onResult: (text: string) => void) => void;
  readonly stopListening: () => void;
  readonly speakTts: (plainText: string) => Promise<void>;
  readonly stopPlayback: () => void;
  /** Any STT path is available — server-side via MediaRecorder OR browser Web Speech. */
  readonly speechRecognitionSupported: boolean;
}

const DEFAULT_FISH_MODEL = "S1";

/**
 * Resolves the STT endpoint URL. Honors `sttEndpoint` override when set
 * (legacy embed parity with `ttsEndpoint`); otherwise derives from the
 * widget's `apiOrigin`. Exported for unit testing.
 */
export function deriveSttEndpoint(config: Pick<WidgetConfig, "apiOrigin" | "sttEndpoint">): string {
  const override = config.sttEndpoint?.trim();
  if (override) return override;
  const base = config.apiOrigin.replace(/\/+$/, "");
  return `${base}/api/engage/speech/stt`;
}

/**
 * Voice layer for the public widget:
 *  - **STT**: server-side ElevenLabs Scribe v1 via `/api/engage/speech/stt`
 *    (recorded via MediaRecorder) — high quality, multilingual, browser-portable.
 *    Falls back to browser Web Speech (`webkitSpeechRecognition`) if MediaRecorder
 *    or `getUserMedia` is unavailable, OR if the server roundtrip fails / returns
 *    no transcript. Fallback preserves TSAI parity for older clients.
 *  - **TTS**: Fish-style HTTP TTS via `/api/engage/speech` with `speechSynthesis`
 *    as the local fallback (unchanged from prior implementation).
 */
export function useWidgetVoice(config: WidgetConfig): UseWidgetVoiceResult {
  const [autoRead, setAutoRead] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const speechRecognitionSupported = useSyncExternalStore(
    subscribeToNoop,
    getAnySttSupportedSnapshot,
    getAnySttSupportedServerSnapshot,
  );

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recorderChunksRef = useRef<BlobPart[]>([]);
  const recorderStreamRef = useRef<MediaStream | null>(null);
  const recorderResultHandlerRef = useRef<((text: string) => void) | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const stopPlayback = useCallback(() => {
    if (typeof window !== "undefined" && typeof window.speechSynthesis !== "undefined") {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* ignore */
      }
    }
    const audio = audioRef.current;
    if (audio) {
      try {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      } catch {
        /* ignore */
      }
    }
    if (objectUrlRef.current) {
      try {
        URL.revokeObjectURL(objectUrlRef.current);
      } catch {
        /* ignore */
      }
      objectUrlRef.current = null;
    }
  }, []);

  const releaseRecorder = useCallback(() => {
    const rec = recorderRef.current;
    recorderRef.current = null;
    recorderChunksRef.current = [];
    recorderResultHandlerRef.current = null;
    if (rec) {
      try {
        if (rec.state !== "inactive") rec.stop();
      } catch {
        /* ignore */
      }
    }
    const stream = recorderStreamRef.current;
    recorderStreamRef.current = null;
    if (stream) {
      try {
        stream.getTracks().forEach((t) => t.stop());
      } catch {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      stopPlayback();
      try {
        recognitionRef.current?.abort();
      } catch {
        /* ignore */
      }
      recognitionRef.current = null;
      releaseRecorder();
    };
  }, [stopPlayback, releaseRecorder]);

  const speakTts = useCallback(
    async (plainText: string): Promise<void> => {
      const text = plainText.trim();
      if (!text) {
        return;
      }
      stopPlayback();

      const endpoint = config.ttsEndpoint?.trim();
      const voiceId = config.fishVoiceId?.trim();
      const model = (config.fishModel?.trim() || DEFAULT_FISH_MODEL).trim();

      if (endpoint) {
        try {
          // `anonKey` was removed from `WidgetEmbedConfig` on 2026-05-25
          // (Andrew confirmed no TSAI customers shipped the legacy embed
          // attribute). TTS is now keyless from the browser: the
          // `/api/engage/speech` Next.js route auths server-side via
          // FISH_API_KEY.
          const headers: Record<string, string> = {
            "content-type": "application/json",
            accept: "audio/mpeg, audio/*, application/json;q=0.9, */*;q=0.8",
          };

          const res = await fetch(endpoint, {
            method: "POST",
            headers,
            credentials: "omit",
            body: JSON.stringify({
              text,
              voice_id: voiceId ?? "",
              voice: voiceId ?? "",
              model,
            }),
          });

          const contentType = (res.headers.get("content-type") || "").toLowerCase();

          if (contentType.includes("application/json")) {
            const rawJson: unknown = await res.json().catch(() => null);
            if (!res.ok) {
              throw new Error(
                typeof rawJson === "object" &&
                  rawJson !== null &&
                  "error" in rawJson &&
                  typeof (rawJson as { error: unknown }).error === "string"
                  ? (rawJson as { error: string }).error
                  : `tts_http_${res.status}`,
              );
            }
            if (rawJson && typeof rawJson === "object") {
              const b64 = pickBase64Audio(rawJson as Record<string, unknown>);
              if (b64) {
                const mime =
                  typeof (rawJson as { mime_type?: unknown }).mime_type === "string"
                    ? String((rawJson as { mime_type: string }).mime_type)
                    : "audio/mpeg";
                const blob = decodeBase64ToBlob(b64, mime);
                const url = URL.createObjectURL(blob);
                objectUrlRef.current = url;
                const audio = new Audio(url);
                audioRef.current = audio;
                await new Promise<void>((resolve, reject) => {
                  audio.onended = () => resolve();
                  audio.onerror = () => reject(new Error("audio_element_error"));
                  void audio.play().catch(reject);
                });
                return;
              }
            }
            throw new Error("tts_json_no_audio");
          }

          if (!res.ok) {
            const snippet = await res.text().catch(() => "");
            throw new Error(`tts_http_${res.status}:${snippet.slice(0, 120)}`);
          }

          const buf = await res.arrayBuffer();
          const blob = new Blob([buf], { type: contentType.split(";")[0] || "audio/mpeg" });
          const url = URL.createObjectURL(blob);
          objectUrlRef.current = url;
          const audio = new Audio(url);
          audioRef.current = audio;
          await new Promise<void>((resolve, reject) => {
            audio.onended = () => resolve();
            audio.onerror = () => reject(new Error("audio_element_error"));
            void audio.play().catch(reject);
          });
          return;
        } catch (e) {
          console.warn("[useWidgetVoice] fish_tts_failed", e instanceof Error ? e.message : e);
          /* fall through to speechSynthesis */
        }
      }

      if (typeof window === "undefined" || typeof window.speechSynthesis === "undefined") {
        return;
      }
      await new Promise<void>((resolve, reject) => {
        try {
          const utter = new SpeechSynthesisUtterance(text);
          utter.onend = () => resolve();
          utter.onerror = () => reject(new Error("speech_synthesis_error"));
          window.speechSynthesis.speak(utter);
        } catch (err) {
          reject(err instanceof Error ? err : new Error("speech_synthesis_throw"));
        }
      });
    },
    [config.fishModel, config.fishVoiceId, config.ttsEndpoint, stopPlayback],
  );

  const startWebSpeech = useCallback(
    (onResult: (text: string) => void): boolean => {
      if (typeof window === "undefined") return false;
      const Ctor = getSpeechRecognitionConstructor();
      if (!Ctor) return false;
      if (!window.isSecureContext) {
        console.warn("[useWidgetVoice] speech_recognition_requires_secure_context");
        return false;
      }

      let instance: SpeechRecognitionLike;
      try {
        instance = new Ctor();
      } catch (e) {
        console.warn(
          "[useWidgetVoice] recognition_ctor_failed",
          e instanceof Error ? e.message : e,
        );
        return false;
      }

      instance.continuous = false;
      instance.interimResults = false;
      instance.lang =
        (typeof navigator !== "undefined" &&
          Array.isArray(navigator.languages) &&
          typeof navigator.languages[0] === "string" &&
          navigator.languages[0]) ||
        (typeof navigator !== "undefined" && typeof navigator.language === "string"
          ? navigator.language
          : "en-US");

      instance.onresult = (ev: SpeechRecognitionEventLike) => {
        try {
          const results = ev.results;
          const last = results[results.length - 1];
          const piece = last?.[0];
          const transcript = typeof piece?.transcript === "string" ? piece.transcript.trim() : "";
          if (transcript) {
            onResult(transcript);
          }
        } catch {
          /* ignore */
        }
      };

      instance.onerror = (ev: SpeechRecognitionErrorEventLike) => {
        console.warn("[useWidgetVoice] recognition_error", ev.error);
        setIsListening(false);
        recognitionRef.current = null;
      };

      instance.onend = () => {
        setIsListening(false);
        recognitionRef.current = null;
      };

      recognitionRef.current = instance;
      try {
        instance.start();
        setIsListening(true);
        return true;
      } catch (e) {
        console.warn(
          "[useWidgetVoice] recognition_start_failed",
          e instanceof Error ? e.message : e,
        );
        recognitionRef.current = null;
        setIsListening(false);
        return false;
      }
    },
    [],
  );

  const stopWebSpeech = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore */
    }
    try {
      recognitionRef.current?.abort();
    } catch {
      /* ignore */
    }
    recognitionRef.current = null;
  }, []);

  const finalizeRecording = useCallback(async () => {
    const rec = recorderRef.current;
    const onResult = recorderResultHandlerRef.current;
    const chunks = recorderChunksRef.current.slice();
    const stream = recorderStreamRef.current;
    recorderChunksRef.current = [];
    recorderResultHandlerRef.current = null;
    recorderRef.current = null;
    recorderStreamRef.current = null;

    if (stream) {
      try {
        stream.getTracks().forEach((t) => t.stop());
      } catch {
        /* ignore */
      }
    }

    if (!rec || !onResult) {
      setIsListening(false);
      setIsTranscribing(false);
      return;
    }

    if (chunks.length === 0) {
      setIsListening(false);
      setIsTranscribing(false);
      return;
    }

    const mime = rec.mimeType || "audio/webm";
    const blob = new Blob(chunks, { type: mime });
    if (blob.size === 0) {
      setIsListening(false);
      setIsTranscribing(false);
      return;
    }

    setIsTranscribing(true);
    try {
      const endpoint = deriveSttEndpoint(config);
      const form = new FormData();
      form.append("file", blob, mime.includes("ogg") ? "voice.ogg" : "voice.webm");

      const res = await fetch(endpoint, {
        method: "POST",
        body: form,
        credentials: "omit",
      });

      if (!res.ok) {
        const snippet = await res.text().catch(() => "");
        console.warn(
          "[useWidgetVoice] server_stt_failed",
          res.status,
          snippet.slice(0, 200),
        );
        // No silent retry to Web Speech here — the user already finished
        // speaking; we can't replay the buffer through `webkitSpeechRecognition`.
        return;
      }

      const json = (await res.json().catch(() => null)) as
        | { transcript?: string; language_code?: string | null }
        | null;
      const transcript = typeof json?.transcript === "string" ? json.transcript.trim() : "";
      if (transcript) {
        onResult(transcript);
      }
    } catch (err) {
      console.warn(
        "[useWidgetVoice] server_stt_throw",
        err instanceof Error ? err.message : err,
      );
    } finally {
      setIsTranscribing(false);
      setIsListening(false);
    }
  }, [config]);

  const startServerStt = useCallback(
    async (onResult: (text: string) => void): Promise<boolean> => {
      if (!hasServerSttCapability()) return false;
      if (typeof window !== "undefined" && !window.isSecureContext) {
        console.warn("[useWidgetVoice] server_stt_requires_secure_context");
        return false;
      }
      let stream: MediaStream;
      try {
        stream = await window.navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        console.warn(
          "[useWidgetVoice] getUserMedia_denied_or_failed",
          err instanceof Error ? err.message : err,
        );
        return false;
      }
      const mime = pickRecorderMime();
      let recorder: MediaRecorder;
      try {
        recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      } catch (err) {
        console.warn(
          "[useWidgetVoice] mediarecorder_ctor_failed",
          err instanceof Error ? err.message : err,
        );
        try {
          stream.getTracks().forEach((t) => t.stop());
        } catch {
          /* ignore */
        }
        return false;
      }

      recorderRef.current = recorder;
      recorderStreamRef.current = stream;
      recorderChunksRef.current = [];
      recorderResultHandlerRef.current = onResult;

      recorder.ondataavailable = (ev: BlobEvent) => {
        if (ev.data && ev.data.size > 0) {
          recorderChunksRef.current.push(ev.data);
        }
      };
      recorder.onerror = (ev: Event) => {
        console.warn("[useWidgetVoice] mediarecorder_error", ev);
        setIsListening(false);
        setIsTranscribing(false);
        releaseRecorder();
      };
      recorder.onstop = () => {
        void finalizeRecording();
      };

      try {
        recorder.start();
        setIsListening(true);
        return true;
      } catch (err) {
        console.warn(
          "[useWidgetVoice] mediarecorder_start_failed",
          err instanceof Error ? err.message : err,
        );
        releaseRecorder();
        return false;
      }
    },
    [finalizeRecording, releaseRecorder],
  );

  const stopListening = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      try {
        rec.stop(); // triggers onstop → finalizeRecording → server STT POST → onResult
        return;
      } catch {
        /* ignore — fall through to web speech stop */
      }
    }
    stopWebSpeech();
    setIsListening(false);
  }, [stopWebSpeech]);

  const startListening = useCallback(
    (onResult: (text: string) => void) => {
      if (typeof window === "undefined") return;
      // Tear down any in-flight session before starting a fresh one.
      stopWebSpeech();
      releaseRecorder();

      void (async () => {
        const startedServer = await startServerStt(onResult);
        if (startedServer) return;
        const startedBrowser = startWebSpeech(onResult);
        if (!startedBrowser) {
          setIsListening(false);
        }
      })();
    },
    [releaseRecorder, startServerStt, startWebSpeech, stopWebSpeech],
  );

  const toggleAutoRead = useCallback(() => {
    setAutoRead((v) => !v);
  }, []);

  return {
    autoRead,
    toggleAutoRead,
    isListening,
    isTranscribing,
    startListening,
    stopListening,
    speakTts,
    stopPlayback,
    speechRecognitionSupported,
  };
}
